'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Eye,
  EyeOff,
  Copy,
  Check,
  ChevronRight,
  Terminal,
  AlertTriangle,
  Activity,
  GitBranch,
  ClipboardList,
  Users,
  LayoutDashboard,
  Zap,
  Shield,
  BookOpen,
  Keyboard,
  PlayCircle,
  CheckCircle2,
  Circle,
  ArrowRight,
  Lock,
  Globe,
  ClipboardCheck,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

interface TeamMember {
  id: string;
  name: string;
  role: string;
  department: string;
  email: string;
  password: string;
  avatar: string;
  deptColor: string;
}

interface WalkthroughStep {
  id: string;
  icon: React.ElementType;
  title: string;
  route: string;
  description: string;
  tips: string[];
  color: string;
}

interface ShortcutGroup {
  group: string;
  shortcuts: { keys: string[]; action: string }[];
}

interface IncidentStep {
  step: number;
  title: string;
  description: string;
  action: string;
  role: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const teamMembers: TeamMember[] = [
  { id: 'tm-1', name: 'Dawit Taddese', role: 'Network Engineer', department: 'Network', email: 'dawit.taddese@ai.nablix.net', password: 'NetOps@2024', avatar: 'DT', deptColor: 'text-primary bg-primary/10 border-primary/20' },
  { id: 'tm-2', name: 'Selam Bekele', role: 'NOC Analyst', department: 'Network', email: 'selam.bekele@ai.nablix.net', password: 'NOC@Selam25', avatar: 'SB', deptColor: 'text-primary bg-primary/10 border-primary/20' },
  { id: 'tm-3', name: 'Yonas Girma', role: 'Systems Administrator', department: 'Infrastructure', email: 'yonas.girma@ai.nablix.net', password: 'SysAdmin@Yonas24', avatar: 'YG', deptColor: 'text-success bg-success/10 border-success/20' },
  { id: 'tm-4', name: 'Hiwot Alemu', role: 'Security Analyst', department: 'Security', email: 'hiwot.alemu@ai.nablix.net', password: 'SecOps@Hiwot25', avatar: 'HA', deptColor: 'text-warning bg-warning/10 border-warning/20' },
  { id: 'tm-5', name: 'Bereket Haile', role: 'DevOps Engineer', department: 'Infrastructure', email: 'bereket.haile@ai.nablix.net', password: 'DevOps@Bereket24', avatar: 'BH', deptColor: 'text-success bg-success/10 border-success/20' },
  { id: 'tm-6', name: 'Tigist Worku', role: 'Network Operations Manager', department: 'Management', email: 'tigist.worku@ai.nablix.net', password: 'Manager@Tigist25', avatar: 'TW', deptColor: 'text-purple-400 bg-purple-400/10 border-purple-400/20' },
  { id: 'tm-7', name: 'Abebe Mekonen', role: 'Incident Responder', department: 'Network', email: 'abebe.mekonen@ai.nablix.net', password: 'Incident@Abebe24', avatar: 'AM', deptColor: 'text-primary bg-primary/10 border-primary/20' },
];

const walkthroughSteps: WalkthroughStep[] = [
  {
    id: 'w-1',
    icon: LayoutDashboard,
    title: 'Network Dashboard',
    route: '/network-operations-dashboard',
    description: 'Your command center. Monitor real-time KPIs, node health, throughput, latency, and active anomalies across all regions.',
    tips: ['Check Health Score first — below 70% triggers escalation', 'Anomaly panel auto-refreshes every 30s', 'Click any region row to drill into node details'],
    color: 'text-primary',
  },
  {
    id: 'w-2',
    icon: AlertTriangle,
    title: 'Incident Management',
    route: '/incident-management',
    description: 'Triage, assign, and resolve incidents. Filter by severity, status, and assignee. Use the detail panel to update status and add notes.',
    tips: ['Critical incidents auto-escalate after 5 min unacknowledged', 'Use bulk-assign for mass triage during outages', 'SLA timer turns red at 80% elapsed'],
    color: 'text-error',
  },
  {
    id: 'w-3',
    icon: Zap,
    title: 'Automation & Integration',
    route: '/automation-integration',
    description: 'View active automation rules, integration health, and job execution history. Enable or disable rules without touching code.',
    tips: ['Green status = rule active and healthy', 'Failed jobs auto-retry 3× before alerting', 'Execution volume chart shows last 7 days'],
    color: 'text-warning',
  },
  {
    id: 'w-4',
    icon: GitBranch,
    title: 'Automation Workflows',
    route: '/automation-workflows',
    description: 'Build and manage multi-step automation workflows. Chain conditions, actions, and notifications into reusable playbooks.',
    tips: ['Drag nodes to reorder workflow steps', 'Test mode runs without triggering real actions', 'Clone existing workflows to speed up creation'],
    color: 'text-success',
  },
  {
    id: 'w-5',
    icon: ClipboardList,
    title: 'Work Order Allocation',
    route: '/work-order-allocation',
    description: 'Assign and prioritize work orders to team members. Track SLA deadlines, capacity, and unassigned queue in one view.',
    tips: ['Red badge = SLA breach imminent', 'Drag-to-assign from unassigned queue', 'Filter by department to see team load'],
    color: 'text-orange-400',
  },
  {
    id: 'w-6',
    icon: ClipboardCheck,
    title: 'Work Order Execution',
    route: '/work-order-execution',
    description: 'Execute assigned work orders step-by-step. Log progress, attach evidence, and mark completion with timestamps.',
    tips: ['Start timer when you begin work — SLA tracks it', 'Add comments for handoff context', 'Upload screenshots as completion evidence'],
    color: 'text-cyan-400',
  },
  {
    id: 'w-7',
    icon: Users,
    title: 'Team',
    route: '/team',
    description: 'Manage team members, on-call schedules, escalation rules, and performance KPIs. View availability and shift coverage.',
    tips: ['On-call schedule updates reflect immediately', 'KPI cards refresh daily at 00:00 UTC+3', 'Escalation rules fire in order of level'],
    color: 'text-purple-400',
  },
  {
    id: 'w-8',
    icon: Globe,
    title: 'Customer Status Portal',
    route: '/incident-status',
    description: 'Public-facing status page for customers. Shows active incidents, maintenance windows, and service health by component.',
    tips: ['Post updates within 15 min of incident declaration', 'Resolved incidents auto-archive after 24h', 'Customers can subscribe to email notifications'],
    color: 'text-teal-400',
  },
];

const shortcutGroups: ShortcutGroup[] = [
  {
    group: 'Navigation',
    shortcuts: [
      { keys: ['G', 'D'], action: 'Go to Network Dashboard' },
      { keys: ['G', 'I'], action: 'Go to Incident Management' },
      { keys: ['G', 'T'], action: 'Go to Team' },
      { keys: ['G', 'W'], action: 'Go to Work Orders' },
      { keys: ['G', 'A'], action: 'Go to Automation' },
    ],
  },
  {
    group: 'Incidents',
    shortcuts: [
      { keys: ['N', 'I'], action: 'New Incident' },
      { keys: ['Ctrl', 'K'], action: 'Quick search incidents' },
      { keys: ['E'], action: 'Escalate selected incident' },
      { keys: ['R'], action: 'Resolve selected incident' },
      { keys: ['A'], action: 'Acknowledge selected incident' },
    ],
  },
  {
    group: 'Work Orders',
    shortcuts: [
      { keys: ['N', 'W'], action: 'New Work Order' },
      { keys: ['Ctrl', 'Enter'], action: 'Save & submit form' },
      { keys: ['Esc'], action: 'Close panel / cancel' },
      { keys: ['Tab'], action: 'Next field in form' },
    ],
  },
  {
    group: 'Dashboard',
    shortcuts: [
      { keys: ['R'], action: 'Refresh dashboard data' },
      { keys: ['F'], action: 'Toggle fullscreen mode' },
      { keys: ['?'], action: 'Show keyboard shortcuts' },
      { keys: ['Ctrl', 'S'], action: 'Save current filter preset' },
    ],
  },
];

const incidentWorkflow: IncidentStep[] = [
  {
    step: 1,
    title: 'Alert Received',
    description: 'System detects anomaly or threshold breach. Alert fires via SMS, email, or in-app notification.',
    action: 'Check your notification channel. Acknowledge within 5 minutes to prevent auto-escalation.',
    role: 'On-call Engineer',
  },
  {
    step: 2,
    title: 'Triage & Classify',
    description: 'Open Incident Management. Assess severity (P1–P4), affected services, and blast radius.',
    action: 'Set severity, add initial description, assign to yourself or appropriate team member.',
    role: 'NOC Analyst / On-call',
  },
  {
    step: 3,
    title: 'Investigate',
    description: 'Use Network Dashboard to correlate metrics. Check anomaly panel, latency spikes, and node health.',
    action: 'Document findings in incident notes. Link related work orders if infrastructure changes are needed.',
    role: 'Network Engineer',
  },
  {
    step: 4,
    title: 'Remediate',
    description: 'Apply fix — manual or via automation workflow. Create work order for infrastructure changes.',
    action: 'Execute remediation steps. Update incident status to "In Progress". Log each action taken.',
    role: 'Network Engineer / DevOps',
  },
  {
    step: 5,
    title: 'Verify & Resolve',
    description: 'Confirm metrics return to normal. Check health score and alert feed for 10 minutes post-fix.',
    action: 'Mark incident Resolved. Add resolution summary and root cause. Close linked work orders.',
    role: 'On-call Engineer',
  },
  {
    step: 6,
    title: 'Post-Incident Review',
    description: 'For P1/P2 incidents: schedule PIR within 48 hours. Document timeline, impact, and prevention steps.',
    action: 'Share PIR report with team. Update runbooks if process gaps were found.',
    role: 'Network Operations Manager',
  },
];

// ─── Sub-components ────────────────────────────────────────────────────────────

function CredentialRow({ member }: { member: TeamMember }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPass, setCopiedPass] = useState(false);

  const copyToClipboard = async (text: string, type: 'email' | 'pass') => {
    await navigator.clipboard.writeText(text);
    if (type === 'email') {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 1500);
    } else {
      setCopiedPass(true);
      setTimeout(() => setCopiedPass(false), 1500);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-raised border border-border hover:border-primary/30 transition-colors">
      {/* Avatar */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold border flex-shrink-0 ${member.deptColor}`}>
        {member.avatar}
      </div>

      {/* Name + Role */}
      <div className="min-w-0 w-32 flex-shrink-0">
        <p className="text-sm font-medium text-foreground truncate">{member.name}</p>
        <p className="text-xs text-muted truncate">{member.role}</p>
      </div>

      {/* Email */}
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <span className="text-xs font-mono text-foreground-muted truncate">{member.email}</span>
        <button
          onClick={() => copyToClipboard(member.email, 'email')}
          className="flex-shrink-0 p-1 rounded hover:bg-primary/10 text-muted hover:text-primary transition-colors"
          title="Copy email"
        >
          {copiedEmail ? <Check size={12} className="text-success" /> : <Copy size={12} />}
        </button>
      </div>

      {/* Password */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-xs font-mono text-foreground-muted w-28 text-right">
          {showPassword ? member.password : '••••••••••••'}
        </span>
        <button
          onClick={() => setShowPassword(!showPassword)}
          className="p-1 rounded hover:bg-primary/10 text-muted hover:text-primary transition-colors"
          title={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
        <button
          onClick={() => copyToClipboard(member.password, 'pass')}
          className="p-1 rounded hover:bg-primary/10 text-muted hover:text-primary transition-colors"
          title="Copy password"
        >
          {copiedPass ? <Check size={12} className="text-success" /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const [activeWalkthrough, setActiveWalkthrough] = useState<string>('w-1');
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const activeStep = walkthroughSteps.find((s) => s.id === activeWalkthrough);

  const toggleIncidentStep = (step: number) => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(step)) next.delete(step);
      else next.add(step);
      return next;
    });
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-8 max-w-7xl mx-auto">

        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BookOpen size={20} className="text-primary" />
              <span className="text-xs font-semibold uppercase tracking-widest text-primary">Getting Started</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground">Platform Onboarding</h1>
            <p className="text-sm text-muted mt-1">
              Everything your team needs to get up and running on NablixAI NetOps.
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20">
            <Shield size={14} className="text-success" />
            <span className="text-xs font-medium text-success">7 members configured</span>
          </div>
        </div>

        {/* ── Section 1: Platform Walkthrough ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <PlayCircle size={16} className="text-primary" />
            <h2 className="text-base font-semibold text-foreground">Platform Walkthrough</h2>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {/* Module list */}
            <div className="col-span-4 space-y-1">
              {walkthroughSteps.map((step) => {
                const Icon = step.icon;
                const isActive = activeWalkthrough === step.id;
                return (
                  <button
                    key={step.id}
                    onClick={() => setActiveWalkthrough(step.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 ${
                      isActive
                        ? 'bg-primary/10 border border-primary/30 text-foreground'
                        : 'hover:bg-surface-raised text-foreground-muted hover:text-foreground border border-transparent'
                    }`}
                  >
                    <Icon size={15} className={isActive ? step.color : 'text-muted'} />
                    <span className="text-sm font-medium truncate">{step.title}</span>
                    {isActive && <ChevronRight size={14} className="ml-auto text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Detail panel */}
            {activeStep && (
              <div className="col-span-8 rounded-xl border border-border bg-surface p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-lg bg-surface-raised border border-border`}>
                    <activeStep.icon size={20} className={activeStep.color} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{activeStep.title}</h3>
                    <code className="text-xs text-muted font-mono">{activeStep.route}</code>
                  </div>
                </div>

                <p className="text-sm text-foreground-muted leading-relaxed">{activeStep.description}</p>

                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted">Pro Tips</p>
                  {activeStep.tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      <p className="text-sm text-foreground-muted">{tip}</p>
                    </div>
                  ))}
                </div>

                <a
                  href={activeStep.route}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/20 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                >
                  Open {activeStep.title}
                  <ArrowRight size={14} />
                </a>
              </div>
            )}
          </div>
        </section>

        {/* ── Section 2: Login Credentials ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Lock size={16} className="text-warning" />
            <h2 className="text-base font-semibold text-foreground">Team Login Credentials</h2>
            <span className="ml-auto text-xs text-muted flex items-center gap-1">
              <Shield size={11} />
              Passwords masked by default — click eye icon to reveal
            </span>
          </div>

          <div className="rounded-xl border border-border bg-surface overflow-hidden">
            {/* Header row */}
            <div className="flex items-center gap-3 px-3 py-2 bg-surface-raised border-b border-border">
              <div className="w-9 flex-shrink-0" />
              <div className="w-32 flex-shrink-0">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted">Member</span>
              </div>
              <div className="flex-1">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted">Email</span>
              </div>
              <div className="flex-shrink-0 w-40 text-right">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted">Password</span>
              </div>
            </div>

            {/* Member rows */}
            <div className="p-3 space-y-2">
              {teamMembers.map((member) => (
                <CredentialRow key={member.id} member={member} />
              ))}
            </div>

            <div className="px-4 py-3 bg-warning/5 border-t border-warning/20 flex items-center gap-2">
              <AlertTriangle size={13} className="text-warning flex-shrink-0" />
              <p className="text-xs text-warning/80">
                Share credentials only through secure channels. Passwords should be changed after first login.
              </p>
            </div>
          </div>
        </section>

        {/* ── Section 3: Keyboard Shortcuts ── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Keyboard size={16} className="text-success" />
            <h2 className="text-base font-semibold text-foreground">Keyboard Shortcuts</h2>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {shortcutGroups.map((group) => (
              <div key={group.group} className="rounded-xl border border-border bg-surface p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Terminal size={13} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">{group.group}</h3>
                </div>
                <div className="space-y-2">
                  {group.shortcuts.map((sc, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <span className="text-xs text-foreground-muted">{sc.action}</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {sc.keys.map((key, ki) => (
                          <React.Fragment key={ki}>
                            <kbd className="px-1.5 py-0.5 rounded bg-surface-raised border border-border text-xs font-mono text-foreground font-medium">
                              {key}
                            </kbd>
                            {ki < sc.keys.length - 1 && (
                              <span className="text-muted text-xs">+</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 4: First-Run Incident Workflow ── */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={16} className="text-error" />
            <h2 className="text-base font-semibold text-foreground">First-Run Incident Workflow</h2>
          </div>
          <p className="text-xs text-muted mb-4 ml-6">
            Follow these steps when you encounter your first incident. Check off each step as you complete it.
          </p>

          <div className="grid grid-cols-2 gap-3">
            {incidentWorkflow.map((item) => {
              const done = completedSteps.has(item.step);
              return (
                <div
                  key={item.step}
                  className={`rounded-xl border p-4 transition-all duration-200 ${
                    done
                      ? 'border-success/30 bg-success/5' :'border-border bg-surface hover:border-primary/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Step number / check */}
                    <button
                      onClick={() => toggleIncidentStep(item.step)}
                      className="flex-shrink-0 mt-0.5"
                      title={done ? 'Mark incomplete' : 'Mark complete'}
                    >
                      {done ? (
                        <CheckCircle2 size={20} className="text-success" />
                      ) : (
                        <Circle size={20} className="text-muted hover:text-primary transition-colors" />
                      )}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-muted">Step {item.step}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-surface-raised border border-border text-muted">
                          {item.role}
                        </span>
                      </div>
                      <h3 className={`text-sm font-semibold mb-1 ${done ? 'text-success line-through decoration-success/50' : 'text-foreground'}`}>
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-muted leading-relaxed mb-2">{item.description}</p>
                      <div className="flex items-start gap-1.5 p-2 rounded-lg bg-surface-raised border border-border">
                        <ArrowRight size={11} className="text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-foreground-muted">{item.action}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-surface-raised overflow-hidden">
              <div
                className="h-full bg-success rounded-full transition-all duration-500"
                style={{ width: `${(completedSteps.size / incidentWorkflow.length) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted font-mono flex-shrink-0">
              {completedSteps.size}/{incidentWorkflow.length} steps
            </span>
          </div>
        </section>

      </div>
    </AppLayout>
  );
}
